import { apiClient } from "../client";

/**
 * The per-school official Walky accounts — one `walkyofficial@<domain>` per
 * school, which staff sign into to act as Walky inside that school.
 */

export interface WalkyAccount {
  _id: string;
  email: string;
  first_name: string;
  avatar_url?: string;
  is_active: boolean;
  campus_id?: string;
  school: {
    _id: string;
    school_name: string;
    email_domain: string;
  } | null;
  /** Whether a password has been set. The hash never leaves the server. */
  has_password: boolean;
  createdAt: string;
}

export interface SyncResult {
  created: number;
  skipped: number;
  /** Schools with no email_domain, so no address could be derived. */
  failed: string[];
}

export interface BroadcastResult {
  school_id: string;
  recipients: number;
  /** How many of the recipients also got a push notification. */
  pushed: number;
  /** Set when this school could not be sent to, e.g. it has no account yet. */
  error?: string;
}

export const walkyAccountsAPI = {
  list: async (): Promise<WalkyAccount[]> => {
    const response = await apiClient.get("/api/admin/walky-accounts");
    return response.data.data;
  },

  /** Creates the account for any school missing one. Idempotent. */
  sync: async (): Promise<SyncResult> => {
    const response = await apiClient.post("/api/admin/walky-accounts/sync");
    return response.data.data;
  },

  /**
   * The only way one of these accounts gets a password — email-based reset
   * stays closed for them. Signs out any existing session on the account.
   */
  setPassword: async (userId: string, password: string): Promise<void> => {
    await apiClient.put(`/api/admin/walky-accounts/${userId}/password`, {
      password,
    });
  },

  /**
   * DMs everyone in each school from that school's official account. With
   * `dryRun`, only counts who would receive it.
   */
  broadcast: async (
    schoolIds: string[],
    content: string,
    dryRun = false,
  ): Promise<BroadcastResult[]> => {
    const response = await apiClient.post(
      "/api/admin/walky-accounts/broadcast",
      { school_ids: schoolIds, content, dry_run: dryRun },
      // A whole school is written in one request; the client's 10s default
      // would give up on a large one while the server is still sending.
      { timeout: 300_000 },
    );
    return response.data.data;
  },
};
